import { createContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { User } from "../interfaces/user";
import { environment } from "../environments/environment";
import { wrapError } from "../utils/errorHandler";

interface UserContextType {
  logout: () => void; // Add logout function to context type
  setUser: (user: User) => void;
  setUserInfo: (user: User | null) => void;
  setUserToken: (token: string) => void;
  user: User | null;
  userInfo: User | null;
  userToken: string | null;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  userInfo: null,
  setUserInfo: () => {},
  userToken: null,
  setUserToken: () => {},
  logout: () => {},
});

export const UserProvider = ({ children }: any) => {
  const [user, setUser] = useState<User | null>(null);
  const [userInfo, setUserInfo] = useState<User | null>({
    name: "",
    email: "",
    image: "",
    role: "",
    createdAt: "",
  } as unknown as User);
  const [userToken, setUserToken] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: any) => {
      if (user) {
        const userRef = doc(db, environment.collections.users, user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserToken(await user.getIdToken());
          const userData = userSnap.data() as User;
          const userName = user.displayName ? user.displayName?.split(" ") : [];
          userData.nickname = user.nickname || "Guest";
          userData.name = userName.slice(0, -2).join(" ");
          userData.lastname = userName?.slice(-2).join(" ");
          const createdAt = userSnap.data().createdAt;
          if (createdAt?.seconds) {
            const year = new Date(createdAt.seconds * 1000).getFullYear();
            userData.createdAt = year.toString(); // Guardar solo el año como string
          }
          userData.email = user.email || "";
          userData.emailVerified = user.emailVerified;
          userData.profilePicture = user.photoURL;
          setUser(userData);
          unsubscribe();
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserToken(null);
      navigate("/login"); // Redirect to login page after logout
    } catch (error) {
      wrapError(error, "Failed to log out");
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        userInfo,
        setUserInfo,
        userToken,
        setUserToken,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
